import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tip } from '../components/ui/tip';
import { confirmDialog } from '../components/ConfirmDialog';
import { StarIcon, CheckBadgeIcon, GiftIcon, CursorArrowRaysIcon, PaperAirplaneIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

type ReviewRow = {
  id: string;
  clientId: string;
  clientName?: string;
  status: string;
  sentAt?: string | null;
  clickedAt?: string | null;
  reviewedAt?: string | null;
  courtesyStatus: string;
  courtesyValue?: string | null;
  courtesyCode?: string | null;
  courtesyExpiresAt?: string | null;
  courtesyUsedAt?: string | null;
  createdAt: string;
};

// Ficha de ROCAM en Maps, pestaña de reseñas (para revisar de un toque)
const MAPS_REVIEWS_URL = 'https://www.google.com/maps/search/?api=1&query=ROCAM+Neumaticos';

const fmtFecha = (v?: string | null) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const Reviews: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get('/api/reviews');
      setStats(res.data.stats || null);
      setRows(res.data.requests || []);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cargar reseñas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const markReviewed = async (row: ReviewRow) => {
    const ok = await confirmDialog({
      title: 'Marcar como reseñado',
      description: `¿${row.clientName || 'Este cliente'} dejó su reseña en Google? Se le habilita la cortesía sorpresa para su próxima visita.`,
      confirmText: 'Sí, reseñó'
    });
    if (!ok) return;
    setBusy(row.id);
    try {
      await axios.post(`/api/reviews/${row.clientId}/mark-reviewed`);
      toast.success('Reseña registrada — cortesía habilitada');
      fetchAll();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error');
    } finally { setBusy(null); }
  };

  const markCourtesyUsed = async (row: ReviewRow) => {
    const ok = await confirmDialog({
      title: 'Marcar cortesía como usada',
      description: `Se marca la cortesía de ${row.clientName || 'este cliente'} (${row.courtesyValue}) como aplicada.`,
      confirmText: 'Marcar usada'
    });
    if (!ok) return;
    setBusy(row.id);
    try {
      await axios.post(`/api/reviews/${row.id}/courtesy-used`);
      toast.success('Cortesía marcada como usada');
      fetchAll();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error');
    } finally { setBusy(null); }
  };

  const statusBadge = (row: ReviewRow) => {
    if (row.reviewedAt) return <Badge variant="secondary" className="bg-emerald-500/15 text-emerald-600"><StarIcon className="h-3 w-3 mr-1" />Reseñó</Badge>;
    if (row.clickedAt) return <Badge variant="secondary" className="bg-blue-500/15 text-blue-600">Clickeó</Badge>;
    if (row.status === 'sent') return <Badge variant="secondary" className="bg-muted text-muted-foreground">Enviado</Badge>;
    if (row.status === 'pending') return <Badge variant="secondary" className="bg-amber-500/15 text-amber-600">Programado</Badge>;
    return <Badge variant="secondary" className="bg-muted text-muted-foreground">{row.status}</Badge>;
  };

  const courtesyBadge = (row: ReviewRow) => {
    if (row.courtesyStatus === 'available') return <Badge variant="secondary" className="bg-violet-500/15 text-violet-600"><GiftIcon className="h-3 w-3 mr-1" />{row.courtesyCode ? `${row.courtesyCode} · ` : ''}{row.courtesyValue} (vence {fmtFecha(row.courtesyExpiresAt)})</Badge>;
    if (row.courtesyStatus === 'used') return <Badge variant="secondary" className="bg-muted text-muted-foreground">Usada {fmtFecha(row.courtesyUsedAt)}</Badge>;
    if (row.courtesyStatus === 'expired') return <Badge variant="secondary" className="bg-muted text-muted-foreground">Vencida</Badge>;
    return <span className="text-muted-foreground text-sm">—</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary" /></div>;
  }

  // Clickearon el link pero todavía no confirmamos la reseña (el paso manual)
  const pendientes = rows.filter((r) => r.clickedAt && !r.reviewedAt);

  const tiles = [
    { label: 'Enviados', value: stats?.enviados, icon: PaperAirplaneIcon },
    { label: 'Clickearon', value: stats?.clickeados, icon: CursorArrowRaysIcon },
    { label: 'Reseñaron', value: stats?.resenados, icon: StarIcon },
    { label: 'Cortesías activas', value: stats?.cortesiasActivas ?? stats?.cortesiasactivas, icon: GiftIcon },
    { label: 'Cortesías usadas', value: stats?.cortesiasUsadas ?? stats?.cortesiasusadas, icon: CheckBadgeIcon }
  ];

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reseñas de Google</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pedido automático post-retiro (un solo mensaje por cliente, para siempre) · cortesía sorpresa al que reseña
          </p>
        </div>
        <Button variant="outline" className="shrink-0" onClick={() => navigate('/reviews/config')}>
          <Cog6ToothIcon className="h-4 w-4 mr-1.5" />
          Configuración
        </Button>
      </div>


      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-lg border bg-card px-3 py-3 shadow-sm">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <t.icon className="h-4 w-4" />{t.label}
            </div>
            <p className="text-2xl font-semibold text-foreground mt-1">{Number(t.value || 0)}</p>
          </div>
        ))}
      </div>

      {/* Esperando confirmación: clickearon el link y todavía no marcamos que reseñaron.
          Es el paso manual del circuito — dos toques: abrir Maps y confirmar. */}
      {pendientes.length > 0 && (
        <div className="rounded-xl border border-blue-500/40 bg-blue-500/5 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-1">
            <CursorArrowRaysIcon className="h-5 w-5 text-blue-500" />
            <h2 className="font-semibold">Esperando confirmación ({pendientes.length})</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Abrieron el link de reseña. Mirá si la reseña está publicada en Google y confirmá: se le crea el cupón y le llega por WhatsApp.
          </p>
          <div className="space-y-2">
            {pendientes.map((row) => (
              <div key={row.id} className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{row.clientName || row.clientId}</p>
                  <p className="text-xs text-muted-foreground">Abrió el link el {fmtFecha(row.clickedAt)}</p>
                </div>
                <Button size="sm" variant="outline" className="h-9" onClick={() => window.open(MAPS_REVIEWS_URL, '_blank')}>
                  Ver en Google
                </Button>
                <Button size="sm" className="h-9" disabled={busy === row.id} onClick={() => markReviewed(row)}>
                  <StarIcon className="h-4 w-4 mr-1" />Reseñó
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-xl border bg-card px-4 py-12 text-center text-muted-foreground">
          <StarIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p>Todavía no hay pedidos de reseña.</p>
          <p className="text-xs mt-1">Se generan solos al confirmar el retiro de un vehículo en Taller.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          {/* Mobile: tarjetas */}
          <div className="md:hidden divide-y divide-border">
            {rows.map((row) => (
              <div key={row.id} className="p-3 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm truncate">{row.clientName || row.clientId}</span>
                  <span className="ml-auto">{statusBadge(row)}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Enviado {fmtFecha(row.sentAt)} · Click {fmtFecha(row.clickedAt)} · Reseña {fmtFecha(row.reviewedAt)}
                </div>
                <div className="flex items-center justify-between gap-2">
                  {courtesyBadge(row)}
                  <div className="flex gap-2">
                    {!row.reviewedAt && row.status !== 'pending' && (
                      <Button size="sm" variant="outline" className="h-8" disabled={busy === row.id} onClick={() => markReviewed(row)}><StarIcon className="h-4 w-4 mr-1" />Reseñó</Button>
                    )}
                    {row.courtesyStatus === 'available' && (
                      <Button size="sm" variant="outline" className="h-8" disabled={busy === row.id} onClick={() => markCourtesyUsed(row)}><GiftIcon className="h-4 w-4 mr-1" />Usada</Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Desktop: tabla */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-muted/50 border-b">
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Cliente</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Enviado</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Click</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Reseña</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Cortesía</TableHead>
                  <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} className="border-b hover:bg-muted/50">
                    <TableCell className="py-1 px-2 align-middle text-left max-w-[220px]">
                      <div className="font-medium truncate">{row.clientName || row.clientId}</div>
                    </TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">{statusBadge(row)}</TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left text-sm">{fmtFecha(row.sentAt)}</TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left text-sm">{fmtFecha(row.clickedAt)}</TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left text-sm">{fmtFecha(row.reviewedAt)}</TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">{courtesyBadge(row)}</TableCell>
                    <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {!row.reviewedAt && row.status !== 'pending' && (
                          <Tip label="Marcar que dejó la reseña (habilita la cortesía)">
                            <Button size="sm" variant="outline" className="h-8" disabled={busy === row.id} onClick={() => markReviewed(row)}><StarIcon className="h-4 w-4 mr-1" />Reseñó</Button>
                          </Tip>
                        )}
                        {row.courtesyStatus === 'available' && (
                          <Tip label="Marcar la cortesía como aplicada">
                            <Button size="sm" variant="outline" className="h-8" disabled={busy === row.id} onClick={() => markCourtesyUsed(row)}><GiftIcon className="h-4 w-4 mr-1" />Usada</Button>
                          </Tip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
